// deps
import React, { ReactElement, useMemo, useState } from "react";

// context
import { UpdateCartContext, UpdateCartContextValue } from "@cart-client/contexts/UpdateCartContext";
import { CouponListingContext } from "@cart-client/contexts/CouponListingContext";

// hooks
import { usePerformGiftingCartUpdate } from "@cart-client/hooks/usePerformGiftingCartUpdate";
import useGiftingApplyCoupon from "@cart-client/hooks/useGiftingApplyCoupon";

// types
import { FlowType } from "@swiggy-private/checkout-core";

export function GiftingCartActionProvider(props: { children: React.ReactNode }): ReactElement {
    // no use for this state in gifting cart action provider
    // keeping it for interface compatibility
    const [showM2Chalfcard, setShowM2Chalfcard] = useState(false);

    // Use gifting-specific cart update hook
    const [{ updateCartInProgress, updateCartData, updateCartError }, doUpdateCart, reloadCart] =
        usePerformGiftingCartUpdate();

    // Use gifting-specific apply coupon hook
    const {
        toggleCouponModal,
        showCouponModal,
        updateCouponState,
        syncApplyCouponCart,
        fetchCoupons,
        isFetching,
        coupons,
        nextBestCoupon,
        errorMessage,
        clearErrorMessage,
        resetCouponListingPage,
        togglingDelightPopup,
        showDelightPopup,
        cards,
        isApplyingCoupon,
        hasInitiallyFetched,
    } = useGiftingApplyCoupon();

    const updateCartContextValue: UpdateCartContextValue = useMemo(
        () => ({
            updateCartInProgress: updateCartInProgress || updateCouponState,
            updateCartData,
            updateCartError,
            doUpdateCart,
            reloadCart,
            toggleCouponModal,
            showCouponModal,
            syncApplyCouponCart,
            resetCouponListingPage,
            showDelightPopup,
            togglingDelightPopup,
            showM2Chalfcard,
            setShowM2Chalfcard,
            showM2ChalfcardOnClick: {
                status: false,
                flowType: FlowType.M2C,
            },
            setShowM2ChalfcardOnClick: () => {
                // No-op for gifting
            },
            showPendingDiscountingComms: () => {
                // No-op for gifting
            },
            handleCouponListingPageCouponRemove: () => {
                // No-op for gifting
            },
            performApplyCoupon: () => Promise.resolve(void 0),
            performRemoveCoupon: () => Promise.resolve(void 0),
        }),
        [
            updateCartInProgress,
            updateCouponState,
            updateCartData,
            updateCartError,
            doUpdateCart,
            reloadCart,
            toggleCouponModal,
            showCouponModal,
            syncApplyCouponCart,
            resetCouponListingPage,
            showDelightPopup,
            togglingDelightPopup,
            showM2Chalfcard,
            setShowM2Chalfcard,
        ],
    );

    const couponListingContextValue = useMemo(
        () => ({
            fetchCoupons,
            isFetching,
            isApplyingCoupon,
            coupons,
            nextBestCoupon,
            errorMessage,
            clearErrorMessage,
            resetCouponListingPage,
            cards,
            hasInitiallyFetched: hasInitiallyFetched,
        }),
        [
            fetchCoupons,
            isFetching,
            isApplyingCoupon,
            coupons,
            nextBestCoupon,
            errorMessage,
            clearErrorMessage,
            resetCouponListingPage,
            cards,
            hasInitiallyFetched,
        ],
    );

    return (
        <UpdateCartContext.Provider value={updateCartContextValue}>
            <CouponListingContext.Provider value={couponListingContextValue}>
                {props.children}
            </CouponListingContext.Provider>
        </UpdateCartContext.Provider>
    );
}
